import { FC, useState } from 'react';
import { useDisclosure } from '@chakra-ui/react';
import { Feature } from '@shinetools/pricing-plan-library';

import useCompanyContext from 'common/hooks/useCompanyContext';
import { useCurrentPricingPlan } from 'common/hooks/useCurrentPricingPlan';
import useToast from 'components/ToastProvider/useToast';
import { logEvent } from 'features/Analytics/analytics';
import { getUploadCompanyLogoFunction } from 'features/Storage/upload';

import ActionsMenu from './components/ActionsMenu';
import DeleteModal from './components/DeleteModal';
import LogoPreviewCard from './components/LogoPreviewCard';
import UploadCard from './components/UploadCard';
import UploadModal from './components/UploadModal';
import UpsellModal from './components/UpsellModal';
import { useUpdateCompanyLogoMutation } from './hooks/useUpdateCompanyLogo';
import locales from './locales';

interface LogoUploadProps {
  logoUrl: string | null;
}

const LogoUpload: FC<LogoUploadProps> = ({ logoUrl }) => {
  const [updateCompanyLogo] = useUpdateCompanyLogoMutation();
  const { companyProfileId } = useCompanyContext();
  const [isUploading, setIsUploading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    isOpen: isUploadModalOpen,
    onClose: closeUploadModal,
    onOpen: openUploadModal,
  } = useDisclosure();

  const {
    isOpen: isDeleteModalOpen,
    onClose: closeDeleteModal,
    onOpen: openDeleteModal,
  } = useDisclosure();

  const {
    isOpen: isUpsellModalOpen,
    onClose: closeUpsellModal,
    onOpen: openUpsellModal,
  } = useDisclosure();

  const { showToast } = useToast();
  const { isFeatureAvailable } = useCurrentPricingPlan();

  const upload = async (logo: File) => {
    if (!companyProfileId) {
      throw new Error('companyProfileId is undefined in UploadModal');
    }

    setIsUploading(true);

    try {
      const uploadedFile =
        await getUploadCompanyLogoFunction(companyProfileId)(logo);

      if (!uploadedFile) {
        setIsUploading(false);
        return;
      }

      await updateCompanyLogo({
        logoBucketName: uploadedFile.metadata.bucket,
        logoFileRef: uploadedFile.metadata.fullPath,
      });

      setIsUploading(false);
      closeUploadModal();
      showToast({ message: locales.successMessage, type: 'success' });
    } catch (err) {
      setIsUploading(false);
      throw err;
    }
  };

  const deleteLogo = async () => {
    try {
      setIsDeleting(true);

      await updateCompanyLogo({ logoBucketName: null, logoFileRef: null });

      closeDeleteModal();
      showToast({ message: locales.successMessage, type: 'success' });
    } catch (error) {
      if (isDeleteModalOpen) {
        closeDeleteModal();
      }

      showToast({ message: locales.deletionErrorMessage, type: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const onDeleteClick = () => {
    openDeleteModal();
    logEvent({ name: 'Logo Delete' });
  };

  const onReplaceClick = () => {
    openUploadModal();
    logEvent({ name: 'Logo Update' });
  };

  const onUploadCardClick = () => {
    if (isFeatureAvailable(Feature.PersonalizedLogo)) {
      openUploadModal();
      logEvent({ name: 'Logo Upload Modal Opened' });

      return;
    }

    openUpsellModal();
    logEvent({ name: 'Logo Upsell Modal Opened' });
  };

  return (
    <>
      {logoUrl && isFeatureAvailable(Feature.PersonalizedLogo) ? (
        <LogoPreviewCard logoUrl={logoUrl}>
          <ActionsMenu
            isLoading={isUploading || isDeleting}
            onDeleteLogo={onDeleteClick}
            onReplaceLogo={onReplaceClick}
          />
        </LogoPreviewCard>
      ) : (
        <UploadCard onClick={onUploadCardClick} />
      )}

      <UploadModal
        isOpen={isUploadModalOpen}
        isUploading={isUploading}
        onClose={closeUploadModal}
        onUpload={upload}
      />

      <DeleteModal
        isDeleting={isDeleting}
        isOpen={isDeleteModalOpen}
        onClose={closeDeleteModal}
        onDelete={deleteLogo}
      />

      <UpsellModal isOpen={isUpsellModalOpen} onClose={closeUpsellModal} />
    </>
  );
};

export default LogoUpload;
